import type { Product } from "@graphql/generated/types";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";

export interface StoreProduct {
    key: Product["key"];
}

const getRecentProducts = (): StoreProduct[] => {
    const recentProducts = localStorage.getItem("recentlyViewed");
    if (!recentProducts) return [];

    try {
        return JSON.parse(recentProducts);
    } catch (error) {
        Logger.error(ServiceType.WEB, "Error parsing recently viewed products from localStorage", {
            tag: LogTag.ERROR,
            error,
        });
        return [];
    }
};

export const useGetRecentlyViewedProducts = (): StoreProduct[] => {
    const [products, setProducts] = useState<StoreProduct[]>([]);
    const { articleNumber } = useParams();

    useEffect(() => {
        if (localStorage) {
            const recentProducts = getRecentProducts();
            setProducts(recentProducts);
        }
        // We want to re-trigger this effect when the user navigates to a another product page
    }, [articleNumber]);

    return products;
};

export const useRecentlyViewedProducts = (productKey: string, maxAmount: number = 20) => {
    useEffect(() => {
        if (!productKey) return;
        const recentProducts = getRecentProducts();
        const productToStore = {
            key: productKey,
        };

        const newRecentProducts: StoreProduct[] = [
            productToStore,
            ...recentProducts.filter((p) => p?.key !== productKey),
        ].slice(0, maxAmount);

        localStorage.setItem("recentlyViewed", JSON.stringify(newRecentProducts));
    }, [maxAmount, productKey]);
};
